import React, { useState, useEffect, useContext } from 'react'
import { HeaderContext } from '../App'
import { connect } from 'react-redux'
import moment from 'moment'
import { ageToday } from '~/helpers/dateAndTime'

import SVGIcon from '~/components/SVGIcon'
import Button from '~/components/Button'
import ErrorMessage from '~/components/ErrorMessage'
import Card from '../components/Card'

// --vars

// --components
function StudentsForm (props) {
  const { students } = props
  const [globalError, setGlobalError] = useState(null)

  const { setHeaderInfo } = useContext(HeaderContext)

  useEffect(() => {
    setHeaderInfo({ pageTitle: 'Student Information' })
  }, [])

  return (
    <div>
      <div className='page students'>

        <div className='page-header'>
          <div className='contents'>
            {students.length === 0 ? 'No Students Found' : 'Your Saved Students'}
          </div>
        </div>

        <div className='page-body'>
          {globalError && <ErrorMessage message={globalError} />}

          {students.length === 0 ? (
            <div className='no-students row'>
              <div>Let's start by registering a student.</div>
              <Button
                unelevated
                label='Add Student'
                icon='person_add'
                onClick={() => {
                  window.scrollTo(0, 0)
                  props.history.push('/addstudent')
                }}
              />
            </div>
          ) : (
            students.map((student, i) =>
              <StudentCard
                key={`student-${student.publicId}-${student.firstName}-${student.lastName}`}
                student={student}
                removeStudent={removeStudent.bind(null, props, setGlobalError, student, i)}
              />
            )
          )}

          {students.length > 0 && (
            <div className='button-container row'>
              <Button
                label='Add Another Student'
                outlined
                icon='person_add'
                onClick={() => {
                  props.history.push('/addstudent')
                  window.scrollTo(0, 0)
                }}
              />
              <Button
                unelevated
                label='Go to Lifelong Learning Courses'
                onClick={() => {
                  props.history.push('/ll-courses')
                  window.scrollTo(0, 0)
                }}
              />
              <Button
                unelevated
                label='Go to Summer Quest Courses'
                onClick={() => {
                  props.history.push('/sq-courses')
                  window.scrollTo(0, 0)
                }}
              />
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

function StudentCard (props) {
  const [expanded, setExpanded] = useState(false)

  const { student, removeStudent } = props

  const {
    firstName,
    middleName,
    lastName,
    gender,
    birthDate,
    contacts,
    phoneNumber,
    optOutOfTextMessages,
    email,
    address1,
    address2,
    city,
    state,
    zipCode,
    publicId,
    notes
  } = student
  const sponsorshipInfo = student.companySponsorshipInfo
  const studentIsSponsored = sponsorshipInfo &&
    (sponsorshipInfo.companyGaveTimeOff ||
    sponsorshipInfo.companyIsPaying ||
    sponsorshipInfo.companyWillReimburse)
  const studentIsAdult = ageToday(birthDate) >= 16

  // checking to see if the student or their contacts
  // have Opted out of text messages to show or hide Asterik note on bottom of card
  const hasOptedOutOfTextMessages = contacts.some(x => x.optOutOfTextMessages === true) || student.optOutOfTextMessages === true

  return (
    <Card className={`card ${expanded ? 'expanded' : 'collapsed'}`} onClick={() => setExpanded(true)}>
      <div className='row'>
        <span className='cell title'>{firstName} {middleName} {lastName}</span>
        <span>
          <Button
            dense
            label='Delete'
            icon='delete'
            onClick={removeStudent}
          />
        </span>
      </div>
      <div className='row'>
        <span className='cell first'>Info</span>
        <span className='cell'>{gender}</span>
        <span className='cell'>{moment(birthDate).format('MM/DD/YYYY')}</span>
      </div>

      {expanded && (
        <>
          {student.contacts.filter(c => c.isPrimaryAdult).map((primary, i) =>
            <div className='row separator' key={`primary-${publicId}-${primary.firstName}-${primary.lastName}`}>
              <span className='cell first'>{`Primary Contact ${i > 0 ? i + 1 : ''}`}{primary.optOutOfTextMessages ? '*' : null}</span>
              <span className='cell'>{primary.firstName} {primary.lastName}</span>
              <span className='cell'>{primary.primaryPhone.number}</span>
            </div>
          )}
          <div className='row separator'>
            <span className='cell first'>Phone{optOutOfTextMessages ? ' *' : null}</span>
            <span className='cell-2'>{phoneNumber}</span>
          </div>
          <div className='row'>
            <span className='cell first'>Email</span>
            <span className='cell-2'>{email}</span>
          </div>
          <div className='row separator'>
            <span className='cell first'>Address</span>
            <span className='cell'>{address1}</span>
            <span className='cell'>{address2}</span>
          </div>
          <div className='row'>
            <span className='cell' />
            <span className='cell'>{city}</span>
            <span className='cell'>{state} {zipCode}</span>
          </div>
          {studentIsAdult && !studentIsSponsored &&
            <div className='row separator'>
              <span className='cell first'>Sponsored by Company</span>
              <span className='cell-2'>no</span>
            </div>}
          {studentIsAdult && studentIsSponsored &&
            <>
              <div className='row separator'>
                <span className='cell first'>Company Name</span>
                <span className='cell-2'>{sponsorshipInfo.companyName}</span>
              </div>
              <div className='row'>
                <span className='cell first'>Company Address</span>
                <span className='cell-2'>{sponsorshipInfo.companyAddress}</span>
              </div>
              <div className='row'>
                <span className='cell' />
                <span className='cell'>{sponsorshipInfo.companyCity}</span>
                <span className='cell'>{sponsorshipInfo.companyState} {sponsorshipInfo.companyZipCode}</span>
              </div>
              <div className='row'>
                <span className='cell first'>Company Gave Time Off</span>
                <span className='cell-2'>{sponsorshipInfo.companyGaveTimeOff ? 'yes' : 'no'}</span>
              </div>
              <div className='row'>
                <span className='cell first'>Company Is Paying</span>
                <span className='cell-2'>{sponsorshipInfo.companyIsPaying ? 'yes' : 'no'}</span>
              </div>
              <div className='row'>
                <span className='cell first'>Company Will Reimburse</span>
                <span className='cell-2'>{sponsorshipInfo.companyWillReimburse ? 'yes' : 'no'}</span>
              </div>
              <div className='row'>
                <span className='cell first'>Is Self Employed</span>
                <span className='cell-2'>{sponsorshipInfo.selfEmployed ? 'yes' : 'no'}</span>
              </div>
              {!sponsorshipInfo.selfEmployed &&
                <>
                  <div className='row'>
                    <span className='cell first'>Supervisor Name</span>
                    <span className='cell-2'>{sponsorshipInfo.supervisorName}</span>
                  </div>
                  <div className='row'>
                    <span className='cell first'>Supervisor Email</span>
                    <span className='cell-2'>{sponsorshipInfo.supervisorEmail}</span>
                  </div>
                  <div className='row'>
                    <span className='cell first'>Supervisor Phone Number</span>
                    <span className='cell-2'>{sponsorshipInfo.supervisorPhoneNumber}</span>
                  </div>
                </>}
            </>}
        </>
      )}

      {contacts.filter(c => c.isEmergencyContact).map((emergency, i) =>
        <div className='row separator' key={`emergency-${student.publicId}-${emergency.firstName}-${emergency.lastName}`}>
          <span className='cell first'>{`Emergency Contact ${i > 0 ? i + 1 : ''}`}{emergency.optOutOfTextMessages ? '*' : null}</span>
          <span className='cell'>{emergency.firstName} {emergency.lastName}</span>
          <span className='cell'>{emergency.primaryPhone.number}</span>
        </div>
      )}

      {expanded && (
        <div className='row separator'>
          <span className='cell first'>Notes</span>
          <span className='cell-2'>{notes}</span>
        </div>
      )}

      {expanded && hasOptedOutOfTextMessages && (
        <div className='row separator'>
          <span className='cell-2'><em>* has Opted out of Text Messages.</em></span>
        </div>
      )}

      {!expanded && (
        <div className='row'>
          <span className='more'>Click to expand<SVGIcon icon='more' className='more-icon' /></span>
        </div>
      )}
    </Card>
  )
}

// --functions
function removeStudent (props, setGlobalError, student, index) {
  if (props.cart.students[student.publicId]) {
    setGlobalError('This student cannot be deleted because your cart contains enrollments for them')
    window.scrollTo(0, 0)
    return
  }
  props.dispatch({
    type: 'REMOVE_STUDENT',
    data: index
  })
  setGlobalError(null)
}

// --mappings
export default connect(state => ({
  cart: state.cart,
  students: state.students.students
}))(StudentsForm)
